/**
 * Currency formatting — INR uses Indian numbering (1,000 · 1,00,000 · 1,00,00,000).
 */
export const INDIAN_LOCALE = 'en-IN';

export function resolveNumberLocale(currency = 'INR') {
  const code = String(currency || 'INR').toUpperCase();
  if (code === 'INR') return INDIAN_LOCALE;
  if (code === 'USD') return 'en-US';
  if (code === 'EUR') return 'de-DE';
  if (code === 'GBP') return 'en-GB';
  if (code === 'CAD') return 'en-CA';
  if (code === 'AUD') return 'en-AU';
  return INDIAN_LOCALE;
}

export function formatIndianNumber(value, options = {}) {
  const n = Number(value);
  if (Number.isNaN(n)) return options.fallback ?? '0';
  const locale = options.locale ?? resolveNumberLocale(options.currency);
  const minimumFractionDigits = options.minimumFractionDigits ?? 0;
  const maximumFractionDigits = options.maximumFractionDigits ?? options.decimals ?? 0;
  return n.toLocaleString(locale, { minimumFractionDigits, maximumFractionDigits });
}

export function formatCurrency(amount, currency = 'INR', options = {}) {
  const n = Number(amount);
  if (Number.isNaN(n)) return options.emptyLabel ?? '—';
  const code = String(currency || 'INR').toUpperCase();
  const locale = resolveNumberLocale(code);
  const minimumFractionDigits = options.minimumFractionDigits ?? 0;
  const maximumFractionDigits = options.maximumFractionDigits ?? options.decimals ?? 2;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(n);
  } catch {
    if (code === 'INR') {
      return `₹${formatIndianNumber(n, { locale: INDIAN_LOCALE, minimumFractionDigits, maximumFractionDigits })}`;
    }
    return `${code} ${formatIndianNumber(n, { locale, minimumFractionDigits, maximumFractionDigits })}`;
  }
}

/** INR shorthand — ₹12,34,567 */
export function formatINR(amount, options = {}) {
  return formatCurrency(amount, 'INR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...options,
  });
}

/** ₹ prefix + Indian grouping (no Intl currency style) */
export function formatRupeeAmount(amount, options = {}) {
  const n = Number(amount) || 0;
  return `₹${formatIndianNumber(n, { locale: INDIAN_LOCALE, ...options })}`;
}

function currencySymbol(code, locale) {
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((part) => part.type === 'currency')?.value || '';
  } catch {
    return '';
  }
}

/** Compact amount for chart labels. INR uses Cr/L; other currencies use M/K. */
export function formatCompactCurrency(amount, currency = 'INR') {
  const code = String(currency || 'INR').toUpperCase();
  if (code === 'INR') return formatCompactINR(amount);
  const n = Number(amount);
  if (!Number.isFinite(n) || n === 0) return '';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  const symbol = currencySymbol(code, resolveNumberLocale(code));
  if (abs >= 1000000) return `${sign}${symbol}${(abs / 1000000).toFixed(abs >= 10000000 ? 0 : 1)}M`;
  if (abs >= 1000) return `${sign}${symbol}${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}K`;
  return formatCurrency(n, code, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

/** Compact INR for chart bar labels — ₹1.2Cr / ₹3.4L / ₹45K / ₹850 */
export function formatCompactINR(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n === 0) return '';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 10000000) return `${sign}₹${(abs / 10000000).toFixed(abs >= 100000000 ? 0 : 1)}Cr`;
  if (abs >= 100000) return `${sign}₹${(abs / 100000).toFixed(abs >= 1000000 ? 0 : 1)}L`;
  if (abs >= 1000) return `${sign}₹${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}K`;
  return `${sign}₹${Math.round(abs)}`;
}
